import { useState, useCallback, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Palette, Upload, X, CheckCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useOrganization, useUpdateOrganization, createLogoUploadURL } from '@/hooks'
import { uploadToS3 } from '@/hooks/useImages'
import { PageHeader, Forbidden } from '@/components'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB for logos

const brandingFormSchema = z.object({
  primary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color (e.g., #3B82F6)')
    .optional()
    .or(z.literal('')),
  secondary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color (e.g., #10B981)')
    .optional()
    .or(z.literal('')),
})

type BrandingFormValues = z.infer<typeof brandingFormSchema>

export function BrandingSettingsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const { data: org, isLoading: orgLoading } = useOrganization(effectiveOrgId)
  const updateMutation = useUpdateOrganization(effectiveOrgId)

  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingFormSchema),
    defaultValues: {
      primary_color: '',
      secondary_color: '',
    },
  })

  // Update form when org data loads
  useEffect(() => {
    if (org) {
      form.reset({
        primary_color: org.primary_color ?? '',
        secondary_color: org.secondary_color ?? '',
      })
      // Set existing logo as preview
      if (org.logo_url) {
        setLogoPreview(org.logo_url)
      }
    }
  }, [org, form])

  const handleLogoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setLogoError(null)

    if (!file) return

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setLogoError('Please select a JPEG, PNG, WebP, or SVG image')
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setLogoError('Logo must be smaller than 2MB')
      return
    }

    // Create preview
    const preview = URL.createObjectURL(file)
    setLogoPreview(preview)
    setLogoFile(file)

    // Reset input
    e.target.value = ''
  }, [])

  const handleLogoRemove = useCallback(() => {
    if (logoPreview && logoFile) {
      URL.revokeObjectURL(logoPreview)
    }
    setLogoPreview(null)
    setLogoFile(null)
    setLogoError(null)
  }, [logoPreview, logoFile])

  const previewPrimaryColor = form.watch('primary_color') || '#0f172a'
  const previewSecondaryColor = form.watch('secondary_color') || '#64748b'

  // Only admin+ can access branding settings
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Forbidden />
  }

  async function onSubmit(values: BrandingFormValues) {
    setSaveSuccess(false)
    setLogoError(null)

    const data: Record<string, string | undefined> = {
      primary_color: values.primary_color || undefined,
      secondary_color: values.secondary_color || undefined,
    }

    // Upload logo if a new file was selected
    if (logoFile) {
      try {
        setUploadProgress(0)
        const { upload_url, logo_key } = await createLogoUploadURL(
          effectiveOrgId,
          logoFile.type,
          logoFile.size
        )
        await uploadToS3(upload_url, logoFile, setUploadProgress)
        data.logo_url = logo_key
      } catch (err) {
        setLogoError(err instanceof Error ? err.message : 'Failed to upload logo')
        setUploadProgress(null)
        return
      } finally {
        setUploadProgress(null)
      }
    }

    // If logo was removed, clear the URL
    if (!logoPreview && org?.logo_url) {
      data.logo_url = ''
    }

    updateMutation.mutate(data, {
      onSuccess: () => {
        setSaveSuccess(true)
        setLogoFile(null)
      },
    })
  }

  if (orgLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Branding Settings"
          description="Customize your organization's appearance"
        />
        <div className="text-muted-foreground">Loading organization settings...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Branding Settings"
        description="Customize your organization's logo and colors"
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Branding Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Brand Colors & Logo
            </CardTitle>
            <CardDescription>
              Set your organization's primary color and logo. These will be used
              throughout the application and on public display pages.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {saveSuccess && (
              <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Branding settings saved successfully!
                </AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Organization Logo</label>
                  <p className="text-sm text-muted-foreground">
                    Upload your company logo (JPEG, PNG, WebP, or SVG, max 2MB)
                  </p>

                  {logoPreview ? (
                    <div className="relative inline-block">
                      <div className="relative w-32 h-32 border rounded-lg overflow-hidden bg-muted">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={handleLogoRemove}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        'flex flex-col items-center justify-center gap-2 w-32 h-32',
                        'rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                        'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
                      )}
                    >
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Upload logo</span>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    onChange={handleLogoSelect}
                    className="hidden"
                  />

                  {logoError && (
                    <p className="text-sm text-destructive">{logoError}</p>
                  )}
                </div>

                {/* Colors */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="primary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Color</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input placeholder="#3B82F6" {...field} />
                          </FormControl>
                          <input
                            type="color"
                            value={field.value || '#3B82F6'}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="h-10 w-10 rounded border cursor-pointer"
                          />
                        </div>
                        <FormDescription>
                          Used for buttons, links, and accents
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Color</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input placeholder="#10B981" {...field} />
                          </FormControl>
                          <input
                            type="color"
                            value={field.value || '#10B981'}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="h-10 w-10 rounded border cursor-pointer"
                          />
                        </div>
                        <FormDescription>
                          Used for secondary elements
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={updateMutation.isPending || uploadProgress !== null}
                >
                  {uploadProgress !== null ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading logo... {uploadProgress}%
                    </>
                  ) : updateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              See how your branding will look on public pages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border overflow-hidden">
              {/* Mock header */}
              <div
                className="p-4 text-white"
                style={{ backgroundColor: previewPrimaryColor }}
              >
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-10 w-10 object-contain bg-white rounded"
                    />
                  ) : (
                    <div className="h-10 w-10 bg-white/20 rounded flex items-center justify-center">
                      <span className="text-xs">Logo</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{org?.name || 'Your Organization'}</h3>
                    <p className="text-sm opacity-80">Cleaning Compliance</p>
                  </div>
                </div>
              </div>

              {/* Mock content */}
              <div className="p-4 bg-background space-y-3">
                <div className="text-sm font-medium">Room Status</div>
                <div className="flex items-center gap-2">
                  <div
                    className="px-3 py-1 rounded-full text-white text-xs"
                    style={{ backgroundColor: previewPrimaryColor }}
                  >
                    Cleaned
                  </div>
                  <div
                    className="px-3 py-1 rounded-full text-white text-xs"
                    style={{ backgroundColor: previewSecondaryColor }}
                  >
                    Pending
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-4">
                  Last cleaned: Today at 2:30 PM
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
